/* ============================================================================
   ✏️✏️✏️   COSMOS ROUTES — MASTER EDIT FILE   ✏️✏️✏️
   This is the ONLY file you need to touch to manage your website content.

   HOW TO EDIT (super simple):
   • Change anything between " " quotes and save — the site updates by itself.
   • ADD A TRIP: copy any { ... } block below, paste it after, change values.
   • REMOVE A TRIP: delete its whole { ... } block (mind the commas).
   • PRICE: plain number only (8499) — the ₹ and commas appear automatically.
   • IMAGE: pick any link from the IMAGE LIBRARY at the bottom of this file,
     or paste any photo URL you like.
   NOTE: the AI chat assistant reads a copy of your trip list from
   /app/backend/server.py (section "TRIPS_BRIEF") — when you change trips or
   prices, update that list too (or ask the agent to sync it).
============================================================================ */

// ---------------------------------------------------------------------------
// 1) YOUR BUSINESS DETAILS — shows in the footer, contact page & WhatsApp
// ---------------------------------------------------------------------------
export const BUSINESS = {
  name: "COSMOS ROUTES",
  tagline: "EXPLORE · EXPERIENCE · BELONG",
  phonePrimary: "8074566187",       // shown on the site
  phoneSecondary: "9347679762",     // shown on the site
  // Booking buttons open WhatsApp chats with this number (91 = India code):
  whatsapp: "918074566187",
  email: "contact@cosmosroutes.com", // ✏️ your work email — shows on contact page & footer
  cities: "Hyderabad, Telangana",   // ✏️ your office location
};

// --- helpers (no need to touch these) ---
export const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
export const durationLabel = (t) => `${t.nights}N / ${t.nights + 1}D`;
export const waLink = (message) =>
  `https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(message)}`;
export const bookWaMessage = (t) =>
  `Hi ${BUSINESS.name}! I want to book the ${t.name} trip (${durationLabel(t)}, ${inr(t.price)} per person). Please share the booking & payment details.`;

// ---------------------------------------------------------------------------
// ✈️  DEPARTURE DAYS — which day each trip starts (auto-applied by duration)
// ---------------------------------------------------------------------------
export const DEPARTURE_DAYS = {
  short2N: "Thursday & Friday",   // all 2N/3D trips
  mid3N: "Thursday",              // all 3N/4D trips
  other: "Wednesday",             // every other trip
};
export const departureLabel = (t) =>
  t.nights === 2 ? DEPARTURE_DAYS.short2N : t.nights === 3 ? DEPARTURE_DAYS.mid3N : DEPARTURE_DAYS.other;

// ---------------------------------------------------------------------------
// 📱 YOUR SOCIAL LINKS — paste your page links between the " " quotes.
//     Leave "" to hide that icon from the footer.
// ---------------------------------------------------------------------------
export const SOCIALS = {
  instagram: "",   // ✏️ e.g. "https://instagram.com/cosmosroutes"
  facebook: "",    // ✏️ e.g. "https://facebook.com/cosmosroutes"
  twitter: "",     // ✏️ e.g. "https://x.com/cosmosroutes"
};

// ---------------------------------------------------------------------------
// 🖼️  IMAGE LIBRARY — copy any link below into a trip's "image" field,
//     or paste your own photo URL.
// ---------------------------------------------------------------------------
export const IMG_URLS = {
  // Your domestic trips
  dandeli: "https://images.unsplash.com/photo-1672517650969-818532fc495e?q=80&w=1600&auto=format&fit=crop",
  kerala: "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?q=80&w=1600&auto=format&fit=crop",
  manali: "https://images.unsplash.com/photo-1605649487212-47bdab064df7?q=80&w=1600&auto=format&fit=crop",
  coorg: "https://images.unsplash.com/photo-1663597675816-9b5d68952f42?q=80&w=1600&auto=format&fit=crop",
  ooty: "https://images.unsplash.com/photo-1709460177357-3e37e2f2abf3?q=80&w=1600&auto=format&fit=crop",
  goa: "https://images.unsplash.com/photo-1698430185962-cbbdc7797f5a?q=80&w=1600&auto=format&fit=crop",
  arunachala: "https://images.unsplash.com/photo-1621488832166-8507b7eae488?q=80&w=1600&auto=format&fit=crop",
  chardham: "https://images.unsplash.com/photo-1742281415064-03dca31f51bb?q=80&w=1600&auto=format&fit=crop",
  meghalaya: "https://images.unsplash.com/photo-1762707675084-01ac3bd4e9f0?q=80&w=1600&auto=format&fit=crop",
  lonavala: "https://images.unsplash.com/photo-1708547981655-e67e6dada4c3?q=80&w=1600&auto=format&fit=crop",
  pondicherry: "https://images.unsplash.com/photo-1724161179561-e9b991c83ec1?q=80&w=1600&auto=format&fit=crop",
  // Spares you can use
  ladakh: "https://images.unsplash.com/photo-1526772662000-3f88f10405ff?q=80&w=1600&auto=format&fit=crop",
  rajasthan: "https://images.unsplash.com/photo-1599661046827-dacff0c0f09a?q=80&w=1600&auto=format&fit=crop",
  spiti: "https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?q=80&w=1600&auto=format&fit=crop",
  // International
  japan: "https://images.unsplash.com/photo-1509023464722-18d996393ca8?crop=entropy&cs=srgb&fm=jpg&q=85",
  iceland: "https://images.unsplash.com/photo-1530841377377-3ff06c0ca713?q=80&w=1600&auto=format&fit=crop",
  swiss: "https://images.unsplash.com/photo-1476514525535-ce74f45814ce?q=80&w=1600&auto=format&fit=crop",
  bali: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?q=80&w=1600&auto=format&fit=crop",
  amalfi: "https://images.unsplash.com/photo-1581416271259-abec87515f51?q=80&w=1600&auto=format&fit=crop",
  // Hero & page banners (used by the site itself)
  hero: "https://images.unsplash.com/photo-1716952589259-5ee61c22d517?crop=entropy&cs=srgb&fm=jpg&q=85",
  expedition: "https://images.unsplash.com/photo-1539635278303-d4002c07eae3?q=80&w=1600&auto=format&fit=crop",
  stargazing: "https://images.unsplash.com/photo-1506744038136-46273834b3fb?q=80&w=1600&auto=format&fit=crop",
  journey: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=1600&auto=format&fit=crop",
};

// ---------------------------------------------------------------------------
// 2) DOMESTIC TRIPS — YOUR REAL TRIPS ✅ (edit freely)
//    region can be anything you like — filter buttons build themselves.
// ---------------------------------------------------------------------------
export const DOMESTIC_PACKAGES = [
  {
    name: "Dandeli Gokarna",
    region: "Karnataka",
    nights: 2,
    price: 8499,
    image: IMG_URLS.dandeli,
    tagline: "River rafting, jungle adventure and a golden sunset on Gokarna's beaches.",
  },
  {
    name: "Kerala",
    region: "South India",
    nights: 5,
    price: 15999,
    image: IMG_URLS.kerala,
    tagline: "Backwaters, tea hills and Munnar mist — God's own country, done slowly.",
  },
  {
    name: "Manali",
    region: "Himalayas",
    nights: 4,
    price: 13999,
    image: IMG_URLS.manali,
    tagline: "Snow peaks, Solang adventures and old-Manali café evenings.",
  },
  {
    name: "Coorg Chikkamagaluru",
    region: "Karnataka",
    nights: 2,
    price: 6999,
    image: IMG_URLS.coorg,
    tagline: "Coffee estates, misty peaks and waterfall stops in the Scotland of India.",
  },
  {
    name: "Ooty Coonoor",
    region: "South India",
    nights: 2,
    price: 7499,
    image: IMG_URLS.ooty,
    tagline: "The Nilgiri mountain railway, tea gardens and lakeside evenings.",
  },
  {
    name: "Goa",
    region: "West India",
    nights: 3,
    price: 11999,
    image: IMG_URLS.goa,
    tagline: "Beach days, café hopping and sunsets that never end.",
  },
  {
    name: "Arunachalam Pondicherry",
    region: "South India",
    nights: 2,
    price: 8499,
    image: IMG_URLS.arunachala,
    tagline: "Temple mornings at Tiruvannamalai and French-quarter evenings by the sea.",
  },
  {
    name: "Chardham",
    region: "Himalayas",
    nights: 11,
    price: 29999,
    image: IMG_URLS.chardham,
    tagline: "Yamunotri, Gangotri, Kedarnath & Badrinath — the complete Himalayan yatra.",
  },
  {
    name: "Meghalaya",
    region: "Northeast",
    nights: 5,
    price: 16999,
    image: IMG_URLS.meghalaya,
    tagline: "Living root bridges, Dawki's crystal river and Shillong café nights.",
  },
  {
    name: "Lonavala",
    region: "West India",
    nights: 2,
    price: 8499,
    image: IMG_URLS.lonavala,
    tagline: "Monsoon waterfalls, hill forts and the classic Sahyadri escape.",
  },
  {
    name: "Pondicherry",
    region: "South India",
    nights: 2, // ✏️ you didn't mention nights for Pondicherry — set the correct one
    price: 7499,
    image: IMG_URLS.pondicherry,
    tagline: "Promenade strolls, Auroville calm and café-hopping by the sea.",
  },
];

// ---------------------------------------------------------------------------
// 3) INTERNATIONAL TRIPS — SAMPLE ⚠️ (edit or delete these and add your own,
//    same format as the domestic trips above)
// ---------------------------------------------------------------------------
export const INTERNATIONAL_PACKAGES = [
  {
    name: "Japan Sacred Autumn",
    region: "Culture & Blossoms",
    nights: 8,
    price: 189999,
    rating: 5.0,
    reviews: 96,
    image: IMG_URLS.japan,
    tagline: "Momiji season, temple mornings and a shinkansen rush past Mt Fuji.",
    highlights: ["Mt Fuji viewpoints", "Kyoto temple mornings", "Nara's bowing deer", "Ryokan onsen night"],
    itinerary: [
      { day: 1, title: "Tokyo Arrival", desc: "Shinjuku neon welcome and a yakitori alley dinner." },
      { day: 2, title: "Old Tokyo", desc: "Asakusa's Senso-ji at dawn, sumo museum and an evening in Shibuya." },
      { day: 3, title: "Fuji Five Lakes", desc: "Kawaguchiko ryokan stay, onsen baths facing the sacred peak." },
      { day: 4, title: "Shinkansen to Kyoto", desc: "Bullet-train ride, Gion lantern-lit evening walk." },
      { day: 5, title: "Kyoto Classics", desc: "Fushimi Inari at sunrise, Kinkaku-ji and a tea ceremony." },
      { day: 6, title: "Arashiyama & Nara", desc: "Bamboo grove morning, then deer of Nara and Todai-ji's great Buddha." },
      { day: 7, title: "Osaka", desc: "Osaka castle and Dotonbori street-food crawl." },
      { day: 8, title: "Miyajima Day Trip", desc: "Peace park reflection and the floating torii gate." },
      { day: 9, title: "Departure", desc: "Kansai airport transfer with one last matcha for the road." },
    ],
  },
  {
    name: "Iceland Aurora & Glaciers",
    region: "Northern Lights",
    nights: 6,
    price: 214999,
    rating: 4.9,
    reviews: 88,
    image: IMG_URLS.iceland,
    tagline: "Chase the aurora across black-sand beaches, ice lagoons and geothermal pools.",
    highlights: ["Aurora hunting nights", "Diamond Beach & Jokulsarlon", "Sky Lagoon soak", "Golden Circle loop"],
    itinerary: [
      { day: 1, title: "Reykjavik Landing", desc: "Harbour dinner and a first northern-lights watch from Grótta." },
      { day: 2, title: "Golden Circle", desc: "Thingvellir rift, Geysir eruptions and Gullfoss in full roar." },
      { day: 3, title: "South Coast", desc: "Seljalandsfoss walk-behind, Reynisfjara black sand and basalt cliffs." },
      { day: 4, title: "Glacier Country", desc: "Glacier hike on Sólheimajökull with certified guides." },
      { day: 5, title: "Jokulsarlon", desc: "Ice-lagoon zodiac, Diamond Beach at low tide, aurora alarm overnight." },
      { day: 6, title: "Return via the Highlands", desc: "Fjaðrárgljúfur canyon and a celebration soak at Sky Lagoon." },
      { day: 7, title: "Departure", desc: "Optional Blue Lagoon stop en route to Keflavík." },
    ],
  },
  {
    name: "Swiss Alpine Rail & Peaks",
    region: "Alpine Luxury",
    nights: 7,
    price: 198999,
    rating: 4.9,
    reviews: 102,
    image: IMG_URLS.swiss,
    tagline: "Panoramic trains, summit sunrises and lakeside villages off the tourist line.",
    highlights: ["Glacier Express leg", "Jungfraujoch 'Top of Europe'", "Lake Oeschinen hike", "Lucerne old town"],
    itinerary: [
      { day: 1, title: "Zurich to Lucerne", desc: "Chapel bridge sunset and lake cruise." },
      { day: 2, title: "Mt Pilatus", desc: "World's steepest cogwheel railway and the Dragon's Path." },
      { day: 3, title: "Interlaken", desc: "Brünig panoramic rail leg and afternoon at Harder Kulm." },
      { day: 4, title: "Jungfraujoch", desc: "Sphinx observatory at 3,571 m and the glacier plateau." },
      { day: 5, title: "Oeschinen Lake", desc: "Alpine lake rowboat and a ridgeline picnic." },
      { day: 6, title: "Glacier Express to Zermatt", desc: "The slowest express train to the car-free Matterhorn village." },
      { day: 7, title: "Gornergrat Sunrise", desc: "First train up for alpenglow on the Matterhorn." },
      { day: 8, title: "Departure", desc: "Rail back to Zurich airport." },
    ],
  },
  {
    name: "Bali Spiritual & Coastal",
    region: "Tropical Sanctuary",
    nights: 6,
    price: 84999,
    rating: 4.8,
    reviews: 245,
    image: IMG_URLS.bali,
    tagline: "Jungle temples, surf mornings and a cliff-top fire dance at Uluwatu.",
    highlights: ["Ubud jungle villa", "Nusa Penida day sail", "Uluwatu kecak at sunset", "Rice-terrace cycling"],
    itinerary: [
      { day: 1, title: "Seminyak Soft Landing", desc: "Beach-club welcome and sunset by the sand." },
      { day: 2, title: "To Ubud", desc: "Tegenungan falls, silver-making workshop, evening Ramayana ballet." },
      { day: 3, title: "Ubud Soul Day", desc: "Sunrise yoga, Campuhan ridge walk and a healer-visit option." },
      { day: 4, title: "Volcano & Terraces", desc: "Mount Batur viewpoint breakfast and Tegalalang terrace cycling." },
      { day: 5, title: "Nusa Penida", desc: "Fast boat to Kelingking and Angel's Billabong." },
      { day: 6, title: "Uluwatu Coast", desc: "Cliff temple, kecak fire dance and a seafood dinner on Jimbaran sand." },
      { day: 7, title: "Departure", desc: "Slow morning, airport transfer." },
    ],
  },
  {
    name: "Amalfi & Dolomites Coast",
    region: "Mediterranean Icons",
    nights: 7,
    price: 174999,
    rating: 4.8,
    reviews: 74,
    image: IMG_URLS.amalfi,
    tagline: "Lemon groves over the Tyrrhenian, then Dolomite peaks the colour of caramel.",
    highlights: ["Positano cliffside stay", "Capri boat day", "Seceda ridgeline cable car", "Alta Badia food trail"],
    itinerary: [
      { day: 1, title: "Naples to Positano", desc: "Private transfer along the Amalfi drive, aperitivo over the sea." },
      { day: 2, title: "Positano & Amalfi", desc: "Path of the Gods morning hike, Ravello's Villa Cimbrone gardens." },
      { day: 3, title: "Capri by Boat", desc: "Blue Grotto swim stop and Faraglioni circumnavigation." },
      { day: 4, title: "Fly North to Bolzano", desc: "Ötzi the Iceman museum and an alpine welcome dinner." },
      { day: 5, title: "Seceda & Val Gardena", desc: "Cable car to the famous ridgeline, rifugio lunch." },
      { day: 6, title: "Alpe di Siusi", desc: "Europe's largest high-meadow at sunrise, e-bike or walk." },
      { day: 7, title: "Lago di Braies", desc: "Rowboat on the emerald lake and a pass-drive to Cortina." },
      { day: 8, title: "Departure", desc: "Transfer to Venice or Innsbruck airport." },
    ],
  },
];

// ---------------------------------------------------------------------------
// 4) GROUP TRIPS — SAMPLE ⚠️ (replace with your fixed-departure dates or delete)
// ---------------------------------------------------------------------------
export const GROUP_TRIPS = [
  {
    id: "spiti-solar-odyssey",
    title: "Spiti Solar Odyssey",
    destination: "Spiti Valley, Himachal",
    dates: "12 – 19 Apr 2026",
    nights: 7,
    price: 29999,
    groupSize: 12,
    spotsLeft: 4,
    host: "Arjun Mehta",
    image: IMG_URLS.spiti,
    vibe: "Mountains & Monasteries",
  },
  {
    id: "meghalaya-monsoon-roots",
    title: "Meghalaya Monsoon Roots",
    destination: "Cherrapunji & Dawki",
    dates: "3 – 8 Jul 2026",
    nights: 5,
    price: 24499,
    groupSize: 10,
    spotsLeft: 6,
    host: "Sara Fernandes",
    image: IMG_URLS.meghalaya,
    vibe: "Rainforests & Rivers",
  },
  {
    id: "bali-soul-retreat",
    title: "Bali Soul Retreat",
    destination: "Ubud & Uluwatu, Bali",
    dates: "9 – 15 Aug 2026",
    nights: 6,
    price: 79999,
    groupSize: 14,
    spotsLeft: 9,
    host: "Sara Fernandes",
    image: IMG_URLS.bali,
    vibe: "Wellness & Islands",
  },
  {
    id: "rajasthan-desert-stars",
    title: "Rajasthan Desert Stars",
    destination: "Jodhpur & Jaisalmer",
    dates: "21 – 28 Nov 2026",
    nights: 7,
    price: 31999,
    groupSize: 12,
    spotsLeft: 8,
    host: "Vikram Rao",
    image: IMG_URLS.rajasthan,
    vibe: "Desert & Heritage",
  },
];

// ---------------------------------------------------------------------------
// 5) TESTIMONIALS — SAMPLE ⚠️ (replace with your real traveller reviews)
// ---------------------------------------------------------------------------
export const TESTIMONIALS = [
  {
    name: "Priya Sharma",
    location: "Mumbai",
    trip: "Ladakh Celestial Valley",
    quote: "I joined alone and came home with eleven friends. The stargazing night is something I will talk about for the rest of my life.",
    initials: "PS",
  },
  {
    name: "Rahul & Anita Verma",
    location: "Bengaluru",
    trip: "Customised Japan Journey",
    quote: "Every detail — from the ryokan to the tea ceremony — felt hand-picked for us. Cosmos Routes turned our anniversary into a legend.",
    initials: "RV",
  },
  {
    name: "Daniel D'Souza",
    location: "Goa",
    trip: "Iceland Aurora & Glaciers",
    quote: "The night the aurora broke over the lagoon, our host had hot chocolate and tripods ready. That is a different level of care.",
    initials: "DD",
  },
  {
    name: "Meera Nair",
    location: "Kochi",
    trip: "Meghalaya Living Roots",
    quote: "Our host treated the local community like family, and by day two so did we. The most responsible trip I have ever taken.",
    initials: "MN",
  },
];

// ---------------------------------------------------------------------------
// 6) WHY US — edit the wording to sound like you
// ---------------------------------------------------------------------------
export const WHY_US = [
  {
    num: "01",
    title: "The Cosmic Standard",
    desc: "Small groups, handpicked stays and routes refined over many departures — never rushed, never generic.",
  },
  {
    num: "02",
    title: "Conscious Travel",
    desc: "Local-first partners and low-impact journeys that give back to the places you fall in love with.",
  },
  {
    num: "03",
    title: "Curated Intimacy",
    desc: "Home-cooked meals, dawn temple visits and hidden viewpoints the guidebooks have not found yet.",
  },
  {
    num: "04",
    title: "24/7 On WhatsApp",
    desc: "A real human on the other end of the chat — from booking to boarding to back home.",
  },
];

// ---------------------------------------------------------------------------
// 7) ABOUT PAGE CONTENT — edit to tell your story
// ---------------------------------------------------------------------------
export const PILLARS = [
  { title: "Curated", desc: "Every route scouted in person, every stay slept in by our own team first." },
  { title: "Conscious", desc: "Local employment and low-impact journeys as standard." },
  { title: "Authentic", desc: "Real culture over checklists — kitchens, courtyards and communities." },
  { title: "Safe", desc: "Certified drivers, vetted stays and support within reach, always." },
];

export const TEAM = [
  { name: "Aarav Mehta", role: "Founder & Chief Route Architect", initials: "AM" },   // ✏️ sample names
  { name: "Sara Fernandes", role: "Head of Curated Journeys", initials: "SF" },       // ✏️ sample names
  { name: "Vikram Rao", role: "Operations & Safety Lead", initials: "VR" },           // ✏️ sample names
];

// ---------------------------------------------------------------------------
// 8) SMALL TEXTS — marquee strip & form dropdown
// ---------------------------------------------------------------------------
export const MARQUEE_ITEMS = [
  "DOMESTIC EXPEDITIONS",
  "INTERNATIONAL ODYSSEYS",
  "BESPOKE JOURNEYS",
  "GROUP DEPARTURES",
  "WEEKEND ESCAPES",
];

export const TRIP_TYPES = [
  "Domestic Package",
  "International Package",
  "Group Trip",
  "Customised Trip",
  "Other",
];

// ---------------------------------------------------------------------------
// 9) WHAT'S INCLUDED / NOT INCLUDED — shows on every trip's detail popup
// ---------------------------------------------------------------------------
export const TRIP_INCLUSIONS = [
  "Handpicked stays & accommodations",
  "All ground transfers & sightseeing",
  "Daily breakfast plus select meals",
  "Dedicated trip host & local experts",
  "All permits & entry fees",
];

export const TRIP_EXCLUSIONS = [
  "Flights / trains to & from the start city",
  "Travel insurance (recommended)",
  "Personal expenses, tips & shopping",
  "Anything not listed under inclusions",
];

// --- internal (no need to touch) ---
export const packageTripType = (pkg) =>
  DOMESTIC_PACKAGES.some((p) => p.name === pkg.name)
    ? "Domestic Package"
    : "International Package";

// keep the old name working too (some pages import IMG)
export const IMG = IMG_URLS;
